import {
  Gift,
  Flower2,
  Smile,
  ShoppingBag,
  Package,
  Music,
  Heart,
  Repeat,
  Users,
  Sparkles,
  Play,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";


const ordinaryItems = [
  {
    icon: Gift,
    title: "Forgotten after the moment",
    description: "Another candle, gift card, or gadget that fades into the background.",
  },
  {
    icon: Flower2,
    title: "Beautiful… but temporary",
    description: "Flowers wilt. Chocolates disappear. The memory fades.",
  },
  {
    icon: Smile,
    title: "The polite smile reaction",
    description: "They appreciate the thought, but it is not something they will talk about later.",
  },
  {
    icon: ShoppingBag,
    title: "Anyone could have bought it",
    description: "There is nothing about it that tells your unique story together.",
  },
  {
    icon: Package,
    title: "Ends up on a shelf",
    description: "Nice for a while, but eventually just another thing in the house.",
  },
];

const songItems = [
  {
    icon: Music,
    title: "Their story turned into music",
    description: "A song written from your memories, milestones, and shared moments.",
  },
  {
    icon: Heart,
    title: "An emotional surprise",
    description: "The moment they hear their story in the lyrics is unforgettable.",
  },
  {
    icon: Repeat,
    title: "Played again and again",
    description: "At anniversaries, family gatherings, road trips, and quiet moments.",
  },
  {
    icon: Users,
    title: "Connects generations",
    description: "A keepsake children and grandchildren can hear years from now.",
  },
  {
    icon: Sparkles,
    title: "A gift no one else could give",
    description: "Because it is built entirely from your relationship and your story.",
  },
];

export default function GiftComparison() {
  return (
    <section className="w-full pt-10 md:pt-13 pb-9 md:pb-11 px-4 md:px-8 bg-card">
      {/* Section Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto text-center mb-14"
      >
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground leading-snug">
          Most gifts are opened once.
        </h2>
        <p className="text-xl md:text-2xl text-muted-foreground mt-2 font-body">
          This one gets replayed for years.
        </p>
      </motion.div>

      {/* Comparison Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Box — Ordinary Gifts */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="rounded-2xl bg-muted/50 border border-border p-8"
        >
          <h3 className="text-lg font-semibold text-muted-foreground uppercase tracking-wide mb-6 font-heading">
            Ordinary Gifts
          </h3>
          <ul className="space-y-6">
            {ordinaryItems.map((item, index) => (
              <li key={index} className="flex items-start gap-4">
                <div className="mt-1 flex-shrink-0">
                  <item.icon className="w-5 h-5 text-muted-foreground/70" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-muted-foreground font-body">{item.title}</p>
                  <p className="text-sm text-muted-foreground/70 mt-0.5 font-body">
                    {item.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Right Box — Your Moment Song */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="rounded-2xl bg-primary/10 border border-primary/30 p-8 relative overflow-hidden"
        >
          {/* Decorative sparkle */}
          <div className="absolute top-4 right-4" aria-hidden="true">
            <Sparkles className="w-6 h-6 text-primary/30" />
          </div>

          <h3 className="text-lg font-semibold text-primary uppercase tracking-wide mb-6 font-heading">
            A Your Moment Song
          </h3>
          <ul className="space-y-6">
            {songItems.map((item, index) => (
              <li key={index} className="flex items-start gap-4">
                <div className="mt-1 flex-shrink-0">
                  <item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-foreground font-body">{item.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5 font-body">
                    {item.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Audio Preview */}
          <button
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-primary bg-primary/15 hover:bg-primary/25 transition-colors rounded-full px-4 py-2 font-body"
            onClick={() => document.getElementById("listen")?.scrollIntoView({ behavior: "smooth" })}
          >
            <Play className="w-4 h-4" fill="currentColor" />
            Hear how it sounds
          </button>
        </motion.div>
      </div>

      {/* Closing Line */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto text-center mt-14"
      >
        <p className="text-lg md:text-xl text-foreground font-medium font-body">
          The difference is not the price.
        </p>
        <p className="text-lg md:text-xl text-muted-foreground font-body">
          It is the meaning behind the gift.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mt-10"
      >
        <Button size="xl" className="rounded-full shadow-lg" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
            Create Your Song
            <ArrowRight className="w-5 h-5" />
        </Button>
        <p className="text-base text-muted-foreground mt-3 font-body">Takes about 5 minutes.</p>
        <p className="text-base text-muted-foreground/70 mt-1 font-body">
          No writing skills needed. Just tell us your story and we will turn it into a song.
        </p>

      </motion.div>

      {/* Value Trust Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto mt-14 rounded-2xl bg-primary/5 border border-primary/15 p-8 md:p-12 text-center"
      >
        <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-3">
          A personalized song that still feels personal
        </h3>
        <p className="text-base text-muted-foreground font-body mb-6 max-w-xl mx-auto">
          Your Moment Song uses technology-assisted music creation with human guidance to turn your story into a meaningful song you can share and keep forever.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-left mb-8 max-w-lg mx-auto">
          {[
            "Lyrics inspired by your story",
            "Technology-assisted music production",
            "Human supported creation",
            "Choose your music style",
            "48–72 hour delivery",
            "Yours forever",
          ].map((item) => (
            <li key={item} className="flex items-center gap-2 text-foreground font-body text-base">
              <span className="text-primary font-bold">✓</span>
              {item}
            </li>
          ))}
        </ul>
        <Button size="xl" className="rounded-full shadow-lg" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
            Create Your Song
            <ArrowRight className="w-5 h-5" />
        </Button>
      </motion.div>
    </section>
  );
}
